import type { ComponentType } from "react";
import { discoverLoader, prefetchDiscoverPage } from "pages/root/DiscoverPage.loader";
import { prefetchHomePage } from "pages/root/home/HomePage.loader";
import { prefetchRewardsIndex } from "pages/root/rewards/RewardsIndex.loader";
import { prefetchShopIndex } from "pages/root/shop/ShopIndex.loader";
import { walletLayoutQuery } from "pages/root/wallet/WalletLayout";
import { SAVE_CONTACT_PATH } from "modules/saveContact/saveContactRoute";
import { queryClient } from "pages/Root";
import { queueTaskInBackground } from "utils/queueTaskInBackground";

export { discoverLoader };

const loadHome = () => import("pages/root/home/HomePage");
const loadDiscover = () => import("pages/root/DiscoverPage");
const loadShop = () => import("pages/root/shop/ShopIndex");
const loadRewards = () => import("pages/root/rewards/RewardsIndex");

const prefetchWallet = () => queryClient.prefetchQuery(walletLayoutQuery);

export const preloadPrimaryRoutes = (initialPathname = globalThis.location.pathname) => {
  // The save-contact page opens the vCard as it loads, and iOS Safari cancels whatever is still in
  // flight; the failed prefetches would then be cached for the tabs the patient continues to.
  if (initialPathname === SAVE_CONTACT_PATH) return;
  queueTaskInBackground(() => {
    void (async () => {
      const components = Promise.allSettled([
        loadHome(),
        loadDiscover(),
        loadShop(),
        loadRewards(),
      ]);
      // The route the patient actually landed on gets its requests in flight first; the rest are
      // warmed behind it so they do not compete for connections with the screen being painted.
      if (initialPathname === "/home") await prefetchHomePage();
      await Promise.allSettled([
        prefetchDiscoverPage(),
        prefetchShopIndex(),
        prefetchRewardsIndex(),
        prefetchWallet(),
        ...(initialPathname === "/home" ? [] : [prefetchHomePage()]),
      ]);
      await components;
    })();
  });
};

const lazy = (load: () => Promise<{ default: ComponentType }>) => async () => ({
  Component: (await load()).default,
});
export const lazyHome = lazy(loadHome);
export const lazyDiscover = lazy(loadDiscover);
export const lazyShop = lazy(loadShop);
export const lazyRewards = lazy(loadRewards);

const preload = (loadComponent: () => Promise<unknown>, loadData: () => unknown) => {
  void Promise.allSettled([loadComponent(), loadData()]);
};
export const preloadHome = () => preload(loadHome, prefetchHomePage);
export const preloadDiscover = () => preload(loadDiscover, prefetchDiscoverPage);
export const preloadShop = () => preload(loadShop, prefetchShopIndex);
export const preloadRewards = () => preload(loadRewards, prefetchRewardsIndex);
export const preloadWallet = prefetchWallet;
